import { ALCHEMISTEN, FileLoader } from 'da-three';
import { Detector } from 'da-three';
import { getLocationVars, loadConfigFromURL } from './service/ConfigHelper.js';
import { createErrorObject, getJSONFromURL, arrayBufferToBase64 } from './../util/Helper.js';
import DAStudio from './DAStudio.js';
import Interface from './Interface.js';
import EventDispatcher from './service/EventDispatcher.js';
import mime from 'mime-types';

class StudioState { 
    static get INITIALIZING() { return Symbol.for('Initializing Studio'); }
    static get ERROR_NO_CONTINUE() { return Symbol.for('Error while initializing Studio'); }
    static get LOADING_CONFIG() { return Symbol.for('Loading Studio config'); }
    static get CONFIG_LOADED() { return Symbol.for('Loaded Studio Config'); }
    static get EMBEDDED_MODE() { return Symbol.for('Studio running in embedded mode'); }
    static get LAUNCHING_STUDIO() { return Symbol.for('Launching Studio with given Config'); }
    static get REQUIRE_LOGIN() { return Symbol.for('Project is secured, require login') }
};

class StudioLauncher {
    constructor() {
        this.readyToLaunch = false;
        this.waitingForLogin = false;
        this.dispatcher = new EventDispatcher();
        this.auth = ALCHEMISTEN.instance.auth;
        this.dispatcher.setEventTypes([
            'status',
            'progress',
            'error',
            'params',
            'login'
        ]);
        this.autoStart = true;
    }

    sendState(state, data) {
        // helper class to send state object
        this.dispatcher.dispatch('status', Object.assign({ state }, data));
    }

    continue(callback) {
        if (this.auth.isLoggedIn) {
            this.prepareConfig(callback);
        }
    }

    launch(callback) {
        // inform listeners that we are initializing
        this.sendState(StudioState.INITIALIZING);

        // first, check if webgl!
        if (!Detector.isWebGLAvailable()) {
            this.dispatcher.dispatch('error', createErrorObject("no_webgl", null));
            // nothing more to do here!
            return;
        }

        // first check if we need to display login form!
        // load all pararms hidden in various places, whatever, ask @viktor
        this.params = getLocationVars();
        console.log("Effective params", this.params);
        this.dispatcher.dispatch('params', this.params);
        var currentEnv = process.env.NODE_ENV;
        this.productionMode = currentEnv !== 'development';

        // check if production oder development build
        if (this.productionMode) {
            // put production specific code here
        } else {
            console.info("DEBUG ===== params", this.params);
            console.info("DEBUG ===== process", process);
        }

        // print version
        console.info(`Running DAStudio v. ${process.env.VERSION} in ${(this.productionMode ? "production" : "development")} mode`);

        if(this.params['appMode'] === true) {
            // AppMode works different!!
            window.WEBGL_APP_MODE = true;
            window.CONTENT_VIEWER = {
                requests: {}
            };

            const onMessageCallback = (event) => {
                let data = typeof event.data === 'object' ? event.data : JSON.parse(event.data);
                const { url, type, _debug, dataUri } = data;
                // this is for debugging
                if(type === 'getData' && _debug === true) {
                    fetch(url)
                    .then(res => res.arrayBuffer())
                    .then(txt => { 
                        return `data:${mime.lookup(url)};base64,${arrayBufferToBase64(txt)}`
                    })
                    .then(debugDataUri => {
                        if (window.CONTENT_VIEWER.requests[url]) {
                            window.CONTENT_VIEWER.requests[url](debugDataUri);
                            delete window.CONTENT_VIEWER.requests[url];
                        }
                    });
                }

                if(type === 'app_getDataResponse') {
                    if (window.CONTENT_VIEWER.requests[url]) {
                        window.CONTENT_VIEWER.requests[url](dataUri);
                        delete window.CONTENT_VIEWER.requests[url];
                    }
                }
            }

            window.addEventListener('message', onMessageCallback);
            document.addEventListener('message', onMessageCallback);

            this.internalLaunch();
        } else {
            var configPath = this.params.API_BASE + this.params.projectID;
            if(this.params.configPath) {
                configPath = this.params.configPath;
            }
            getJSONFromURL(configPath, (data) => {
                if (data.payload) {
                    let res = data.payload;
                    this.prepareConfig(callback);
                    //
                    // if (res.public || this.auth.isLoggedIn) {
                    //     this.prepareConfig(callback);
                    // } else {
                    //     this.waitingForLogin = true;
                    //     this.sendState(StudioState.REQUIRE_LOGIN);
                    // }
                } else {
                    this.sendState(StudioState.ERROR_NO_CONTINUE, null);
                    this.dispatcher.dispatch('error', {
                        "message": "unknown fail"
                    });
                }
            });
        }
    }

    prepareConfig(callback) {
        if (process.env.IS_PLUGIN) {
            // deferred launch waiting for plugins to complete
            ALCHEMISTEN.instance.pluginRegistry.onPluginInitComplete(locators => {
                
                let config = {};
                if (locators.config) {
                    console.log("Resolving config via plugin providers");
                    this.sendState(StudioState.LOADING_CONFIG, { projectID: this.params.projectID });

                    //this.dispatcher.dispatch('progress', progress);

                    
                    let promises = [];
                    locators.config.forEach(configResolver => {
                        console.log("Passing config through", configResolver.plugin.name);
                        promises.push(configResolver.plugin.getConfig(this.params, config));
                    });

                    Promise.all(promises).then(values => {
                        let mergedConfig = {};
                        values.forEach(val => {
                            mergedConfig = Object.assign(mergedConfig, val);
                        });
                        this.additionalConfig = mergedConfig;
                        this.internalLaunch();
                    }, error => {
                        console.error("Failed to load additional config", error);
                        this.internalLaunch();
                    }); 
                } else {
                    this.internalLaunch();
                }
            });
        } else {
            this.internalLaunch();
        }
    }

    internalLaunch() {
        // Default studio launch
        // first check if hidden element contains a config
        // if call is hosted by alchemisten.de with proper project ID, load it from config URL
        if (this.params.projectID !== undefined) {
            this.sendState(StudioState.LOADING_CONFIG, { projectID: this.params.projectID });
            var configPath = this.params.API_BASE + this.params.projectID;
            if(this.params.configPath) {
                configPath = this.params.configPath;
            }
            loadConfigFromURL(configPath,
                this.configHandler.bind(this),
                this.configProgress.bind(this));
        } else {
            this.sendState(StudioState.ERROR_NO_CONTINUE, null);
            this.dispatcher.dispatch('error', createErrorObject("no_project_id", null));
        }
    }

    launchStudio() {
        if (this.readyToLaunch) {

            this.config.showLanguage = !(this.params.logo === false && this.params.language);

            this.studio = new DAStudio(
                document.getElementById('root'),
                this.config
            );
    
            // assign custom file loader (if auth)
            this.studio.sceneMan.fileLoader = new FileLoader();

            
            //Initialize interface
            this.overlay = new Interface(this.studio);
            if (this.params.scroll !== undefined){
                this.studio.toggleZoom(this.params.scroll === true);
            }

            // bind plugin launcher
            ALCHEMISTEN.instance.pluginRegistry.bindStudio(this.studio);

            this.studio.onWindowResize();
            this.studio.sceneMan.loadObjFile();

            // for debugging
            if (this.config.tech.debug === true) {
                window.studio = this.studio;
                console.info(this.studio);
            }

            this.sendState(StudioState.LAUNCHING_STUDIO);
            this.studio.onWindowResize();
        } else {
            // TODO dispatch error if someone tries to start the studio, if it is not ready yet
        }
    }

    configHandler(config, error) {
        if (error === null) {
            this.config = config;
            if (this.additionalConfig) {
                this.config = Object.assign(this.config, this.additionalConfig);
            }

            // production specific code again
            if (this.productionMode) {
                this.config.tech.debug = false;
            }

            // overwrite some params to config
            if (this.params.debug !== undefined) {
                this.config.tech.debug = this.params.debug;
            }
            if (this.params.transparent !== undefined) {
                this.config.features.useTransparency.active = this.params.transparent;
            }
            
            if(this.params.baseUrl) {
                this.config.project.basedir = this.params.baseUrl;
            }

            if(this.params.projectFolder) {
                this.config.project.folder = this.params.projectFolder;
            }

            if(this.params.staticFolder) {
                this.config.project.static = this.params.staticFolder;
            } else if(this.config.project.static === undefined){
                this.config.project.static = 'static/';
            }

            //Setup language translation
            ALCHEMISTEN.instance.i18n.register(this.config.i18n, this.config.project.projectID);
            if (this.params.language !== undefined
                && this.config.project.languages.indexOf(this.params.language) !== -1) {
                ALCHEMISTEN.instance.i18n.setLanguage(this.params.language);
            }
            this.sendState(StudioState.CONFIG_LOADED, { config: this.config });


            // if embed parameter is not set -> do not wait
            this.readyToLaunch = true;
            if (!this.params.embed) {
                if (this.autoStart) {
                    this.launchStudio(config);
                }
                
            } else {
                // otherwise wait for start notify this via embedded state
                this.sendState(StudioState.EMBEDDED_MODE);
            }

        } else {
            this.sendState(StudioState.ERROR_NO_CONTINUE, null);
            this.dispatcher.dispatch('error', error);
        }
    }

    configProgress(progress) {
        this.dispatcher.dispatch('progress', progress);
    }
};

export {
    StudioLauncher,
    StudioState
};


// WEBPACK FOOTER //
// ./src/js/studio/StudioLauncher.js